import React, { useRef, useState, useMemo } from 'react';
import { toPng } from 'html-to-image';
import { Camera, Check, Clock, Palette } from 'lucide-react';
import type { Todo, ColorTheme } from './types';

interface TodoScreenshotProps {
  todos: Todo[];
  name: string;
}

const difficultyTextEmojis = ['😊', '😐', '😣'];

const colorThemes: ColorTheme[] = [
  {
    name: 'Purple Dream',
    from: 'from-purple-600',
    via: 'via-violet-800',
    to: 'to-indigo-900'
  },
  {
    name: 'Ocean Depths',
    from: 'from-cyan-500',
    via: 'via-blue-700',
    to: 'to-blue-900'
  },
  {
    name: 'Sunset Glow',
    from: 'from-orange-400',
    via: 'via-pink-500',
    to: 'to-rose-700'
  },
  {
    name: 'Forest Mist',
    from: 'from-emerald-400',
    via: 'via-green-600',
    to: 'to-teal-800'
  },
  {
    name: 'Golden Hour',
    from: 'from-amber-300',
    via: 'via-orange-600',
    to: 'to-rose-800'
  },
  {
    name: 'Midnight',
    from: 'from-slate-600',
    via: 'via-slate-800',
    to: 'to-slate-900'
  }
];

// Tag colors that complement our themes
const tagColors = [
  'bg-emerald-500/30 text-emerald-200 ring-1 ring-emerald-500/30',
  'bg-blue-500/30 text-blue-200 ring-1 ring-blue-500/30',
  'bg-purple-500/30 text-purple-200 ring-1 ring-purple-500/30',
  'bg-rose-500/30 text-rose-200 ring-1 ring-rose-500/30',
  'bg-amber-500/30 text-amber-200 ring-1 ring-amber-500/30',
  'bg-cyan-500/30 text-cyan-200 ring-1 ring-cyan-500/30',
  'bg-pink-500/30 text-pink-200 ring-1 ring-pink-500/30',
  'bg-indigo-500/30 text-indigo-200 ring-1 ring-indigo-500/30'
];

export function TodoScreenshot({ todos, name }: TodoScreenshotProps) {
  const [selectedTheme, setSelectedTheme] = useState<ColorTheme>(colorThemes[0]);
  const screenshotRef = useRef<HTMLDivElement>(null);

  // Create a map of tags to their assigned colors
  const tagColorMap = useMemo(() => {
    const map = new Map<string, string>();
    let colorIndex = 0;

    todos.forEach(todo => {
      const matches = todo.text.match(/\[(.*?)\]/g);
      if (matches) {
        matches.forEach(match => {
          if (!map.has(match)) {
            map.set(match, tagColors[colorIndex % tagColors.length]);
            colorIndex++;
          }
        });
      }
    });

    return map;
  }, [todos]);

  // Function to render text with styled tags
  const renderTextWithTags = (text: string) => {
    const parts = text.split(/(\[.*?\])/g);
    return parts.map((part, index) => {
      if (part.match(/^\[.*?\]$/)) {
        const tagColor = tagColorMap.get(part) || tagColors[0];
        return (
          <span
            key={index}
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-sm font-medium mx-1 ${tagColor}`}
          >
            {part.slice(1, -1)}
          </span>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };

  const copyScreenshot = async () => {
    if (!screenshotRef.current) return;
    
    try {
      const dataUrl = await toPng(screenshotRef.current, {
        quality: 1.0,
        pixelRatio: 2,
        style: {
          transform: 'scale(1)',
          transformOrigin: 'top left'
        }
      });
      
      const blob = await fetch(dataUrl).then(res => res.blob());
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': blob })
      ]);
    } catch (error) {
      console.error('Error generating screenshot:', error);
    }
  };

  // Calculate dynamic height based on number of todos
  const baseHeight = 140; // Header space
  const todoHeight = 72; // Height per todo item
  const minHeight = 400;
  const maxHeight = 2000;
  const calculatedHeight = baseHeight + (todos.length * todoHeight);
  const listHeight = Math.max(minHeight, Math.min(calculatedHeight, maxHeight));

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <button
          onClick={copyScreenshot}
          className="flex items-center gap-2 bg-[#0a0a0a] text-[#a0a0a0] border border-[#404040] px-4 py-1 hover:border-[#606060] hover:text-[#c0c0c0]"
        >
          <Camera className="w-4 h-4" />
          Copy as screenshot
        </button>

        <div className="flex items-center gap-2">
          <Palette className="w-4 h-4 text-[#a0a0a0]" />
          <select
            value={selectedTheme.name}
            onChange={(e) => setSelectedTheme(colorThemes.find(theme => theme.name === e.target.value) || colorThemes[0])}
            className="bg-[#0a0a0a] text-[#a0a0a0] border border-[#404040] px-2 py-1 hover:border-[#606060] terminal-input"
          >
            {colorThemes.map(theme => (
              <option key={theme.name} value={theme.name}>
                {theme.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="w-[800px]">
        <div 
          ref={screenshotRef}
          className={`p-10 bg-gradient-to-br ${selectedTheme.from} ${selectedTheme.via} ${selectedTheme.to}`}
        >
          <div 
            style={{ height: `${listHeight}px`, fontFamily: "'Poppins', sans-serif" }}
            className="w-full rounded-xl bg-gradient-to-b from-white/10 to-white/5 backdrop-blur-sm shadow-2xl overflow-hidden"
          >
            <div className="p-8">
              <h2 className="text-3xl font-bold text-white mb-8">
                {name}'s Todo List
              </h2>

              <div className="space-y-3">
                {todos.map((todo) => (
                  <div
                    key={todo.id}
                    className="group flex items-center gap-4 rounded-lg bg-white/[0.03] px-6 py-4 backdrop-blur-sm transition-all duration-300 hover:bg-white/[0.05] border border-white/10"
                  >
                    <div className={`flex h-6 w-6 items-center justify-center rounded-full transition-colors ${
                      todo.completed 
                        ? 'bg-green-500/20 text-green-400 ring-1 ring-green-500/50' 
                        : 'bg-white/5 text-white/20 ring-1 ring-white/10'
                    }`}>
                      <Check className="h-4 w-4" />
                    </div>
                    
                    <span className={`flex-1 text-xl font-medium ${
                      todo.completed 
                        ? 'text-white/40 line-through' 
                        : 'text-white/90'
                    }`}>
                      {renderTextWithTags(todo.text)}
                    </span>

                    <span className="text-xl">
                      {difficultyTextEmojis[todo.difficulty]}
                    </span>

                    <div className="flex items-center gap-2 text-white/70">
                      <Clock className="h-5 w-5" />
                      <span className="font-medium text-lg">{todo.timeEstimate}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}